import { imgixHost } from 'src/config'
import Generic from 'src/models/generic/Generic'
import InventoryRecipe from 'src/models/inventory-recipe/InventoryRecipe'
import Item from 'src/models/item/Item'
import Model, { encoders, executeMutation, GqlError, ID, Integer, JsonArray, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import ItemOrderItemModifier from 'src/models/order-item/ItemOrderItemModifier'
import OrderItem from 'src/models/order-item/OrderItem'
import PriceTierPrice from 'src/models/price-tier/PriceTierPrice'

const ACTIONS = {
  DISABLE_SALES: 'disable_sales',
  ENABLE_SALES: 'enable_sales'
}

export default class ItemVariation extends Model {
  static name = 'ItemVariation'

  static isConfiguration = true

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Concession]

  static define () {
    this.concerns = [Archivable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      action: {
        type: String,
        input: true,
        default: null
      },
      item: {
        type: Item,
        reference: true,
        output: true
      },
      name: {
        type: String,
        required: true,
        translatable: true
      },
      image: {
        type: String,
        default: null
      },
      color: {
        type: String,
        default: null
      },
      description: {
        type: String,
        default: null
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      upcCode: {
        type: String,
        required: true
      },
      price: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      cost: {
        type: Number,
        encode: encoders.cents,
        default: null
      },
      disableEditCostCents: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },
      salesDisabled: {
        type: Boolean,
        default: false
      },
      hasRecipe: {
        type: Boolean,
        output: true,
        default: false
      },
      inventoryRecipes: {
        type: [InventoryRecipe],
        output: true,
        excludeFromDefault: true
      },
      priceTierPrices: {
        type: [PriceTierPrice],
        allowDestroy: true,
        acceptsNestedAttributes: true
      },
      inventoryAvailabilitiesAndConstraintsByLocationAndRegisterStation: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      libraryParentId: {
        type: ID,
        default: null,
        output: true,
        excludeFromDefault: true
      },
      receiveLibraryUpdates: {
        type: Boolean,
        default: false,
        output: true,
        excludeFromDefault: true
      }
    }

    this.computed = {
      nameWithItem () {
        return `${this.item?.name} - ${this.name}`
      },
      backgroundForImgix () {
        if (this.color) {
          return this.color.slice(1)
        } else {
          return 'FFFFFF'
        }
      },
      imageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?w=100&h100&fit=crop&fill=solid&fill-color=${this.backgroundForImgix}&bg=${this.backgroundForImgix}`
      },
      inventoryStrategy () {
        return this.configuration.currentSite?.activeInventoryStrategy
      }
    }

    this.mutations = {
      addOrUpdateItemVariationPriceTiers: {
        arguments: {
          itemVariations: {
            type: JsonArray,
            required: true
          }
        },
        results: {
          success: Boolean,
          errors: [GqlError]
        }
      }
    }

    this.queries = {
      itemVariationByUpc: {
        arguments: {
          upcCode: {
            type: String,
            required: true
          }
        },
        results: {
          itemVariation: ItemVariation
        }
      },
      itemVariationAvailabilitiesAndConstraintsByLocationAndRegisterStation: {
        results: {
          value: Generic
        }
      }
    }
  }

  customSortValue (itemVariation) {
    return itemVariation?.item?.name
  }

  get searchString () {
    return `${this.name} ${this.item.searchString}`.toLowerCase()
  }

  saveItemVariationPriceTiers () {
    if (!this.isCollection) {
      return
    }

    const dirtyItemVariations = _(this.data).map(itemVariation => {
      if (itemVariation.isDirty) {
        return {
          id: itemVariation.id,
          priceTierPrices: _(itemVariation.priceTierPrices.data).map(priceTierPrice => {
            if (priceTierPrice.isRemote && (_.isNil(priceTierPrice.price) || priceTierPrice.price === '')) {
              return { id: priceTierPrice.id, _destroy: true }
            } else if (priceTierPrice.isDirty) {
              return priceTierPrice.deepEncode
            } else {
              return null
            }
          }).compact().value()
        }
      } else {
        return null
      }
    }).compact().value()

    const response = executeMutation('addOrUpdateItemVariationPriceTiers', {
      itemVariations: dirtyItemVariations
    },
    {
      success: Boolean,
      errors: {
        structure: {
          code: true,
          description: true
        }
      }
    })

    return response
  }

  invalidInventoryRecipeIds (selectedModifierIds = []) {
    if (_.isEmpty(selectedModifierIds)) return []

    /**
     * Recipe triggers that specify an unselected modifier are invalid.
     */
    return _(this.inventoryRecipes.data)
      .map(inventoryRecipe => {
        return _.filter(inventoryRecipe.inventoryRecipeTriggers.data, inventoryRecipeTrigger => {
          return inventoryRecipeTrigger.modifierId && !_.includes(selectedModifierIds, inventoryRecipeTrigger.modifierId)
            ? inventoryRecipe.id
            : null
        })
      })
      .flatten()
      .compact()
      .uniq()
      .value()
  }

  availableStock ({
    availabilitiesAndConstraints,
    deliveryType,
    screen,
    selectedModifierIds = []
  }) {
    if (!this.inventoryStrategy || !this.hasRecipe || !availabilitiesAndConstraints) return null

    const sourceLocationKey = this.inventoryStrategy.sourceLocationKey({ deliveryType, item: this.item, screen })
    const sourceLocationId = this.inventoryStrategy.sourceLocationId({ deliveryType, item: this.item, screen })

    return _(availabilitiesAndConstraints[this.id][sourceLocationKey][sourceLocationId])
      .entries()
      .map(([inventoryRecipeId, availabilityAndConstraintsObject]) => {
        return _.includes(this.invalidInventoryRecipeIds(selectedModifierIds), +inventoryRecipeId)
          ? null
          : `${availabilityAndConstraintsObject.available}`
      })
      .compact()
      .max()
  }

  isOutOfStock ({
    availabilitiesAndConstraints,
    deliveryType,
    screen,
    selectedModifierIds = []
  }) {
    if (!this.inventoryStrategy || !this.hasRecipe || !availabilitiesAndConstraints) return false

    return this.availableStock({
      availabilitiesAndConstraints,
      deliveryType,
      screen,
      selectedModifierIds
    }) <= 0
  }

  createOrderItemForSelf () {
    const defaultItemModifier = _(this.item.itemModifierSets.data).flatMap(itemModifierSet =>
      itemModifierSet.defaultModifiers.data.map(defaultModifier => {
        return new ItemOrderItemModifier({
          modifierId: defaultModifier.id,
          modifierSetId: itemModifierSet.modifierSetId
        })
      })
    ).value()

    return new OrderItem({
      type: 'item',
      itemOrderItem: {
        itemId: this.item.id,
        itemVariationId: this.id,
        itemModifiers: defaultItemModifier
      },
      voucher: null,
      voucherType: null,
      discount: null
    })
  }

  async disableSales () {
    this.action = ACTIONS.DISABLE_SALES

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async enableSales () {
    this.action = ACTIONS.ENABLE_SALES

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }
}
